import { ChangeEventHandler, useMemo, useState } from "react";

import { useDebounce } from "../../../hooks";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  SortQuery,
  setPlayFilter,
  useGetAllSeasonNamesQuery,
  useGetAllTagNamesQuery,
  useGetAllTeamsQuery,
} from "../../../redux/slices";
import { useGetAllSeriesQuery } from "../../../redux/slices/series-api";
import { useMultiSelect } from "../../multi-select/hooks";

// Custom sorting function to extract the year from string and compare
function sortYearsDesc(year: string) {
  return parseInt(year.split("-")[1] || ""); // Extract the second year and convert to integer
}

export const useTableFilters = () => {
  const {
    selectedSeason,
    selectedTeam,
    selectedSeries,
    selectedTag,
    selectedPlayNameFilter = "",
  } = useAppSelector((state) => state.playFilter);

  const dispatch = useAppDispatch();

  const onPlayNameFilterChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    dispatch(setPlayFilter({ selectedPlayNameFilter: event.target.value }));
  };

  const playNameFilterDebounced = useDebounce(selectedPlayNameFilter, 300);

  const { data: seasons } = useGetAllSeasonNamesQuery();
  const { data: teams } = useGetAllTeamsQuery();
  const { data: series } = useGetAllSeriesQuery();
  const { data: tags } = useGetAllTagNamesQuery();

  const teamSelections = useMemo(
    () =>
      teams
        ? [...teams]
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map((team) => ({ ...team, label: team.name }))
        : [],
    [teams]
  );
  const seriesSelections = useMemo(
    () =>
      series
        ? [...series]
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map((team) => ({ ...team, label: team.name }))
        : [],
    [series]
  );
  const seasonSelections = useMemo(
    () =>
      seasons
        ? [...seasons]
            .sort((a, b) => sortYearsDesc(b) - sortYearsDesc(a))
            .map((season, id) => ({ id: `${id}`, label: season }))
        : [],
    [seasons]
  );
  const tagSelections = useMemo(
    () =>
      tags
        ? [...tags]
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .map((tag, id) => ({ id: `${id}`, label: tag }))
        : [],
    [tags]
  );

  const seasonMultiSelect = useMultiSelect({
    items: seasonSelections,
    queryName: "seasonName",
    isSingleSelect: true,
    initialSelectedItems: selectedSeason ? [selectedSeason] : undefined,
    onChange: (items) => {
      dispatch(setPlayFilter({ selectedSeason: items[0] }));
    },
  });
  const teamsMultiSelect = useMultiSelect({
    items: teamSelections,
    queryName: "teamName",
    isSingleSelect: true,
    initialSelectedItems: selectedTeam ? [selectedTeam] : undefined,
    onChange: (items) => {
      dispatch(setPlayFilter({ selectedTeam: items[0] }));
    },
  });
  const seriesMultiSelect = useMultiSelect({
    items: seriesSelections,
    queryName: "seriesName",
    isSingleSelect: true,
    initialSelectedItems: selectedSeries ? [selectedSeries] : undefined,
    onChange: (items) => {
      dispatch(setPlayFilter({ selectedSeries: items[0] }));
    },
  });
  const tagsMultiSelect = useMultiSelect({
    items: tagSelections,
    queryName: "tagName",
    isSingleSelect: true,
    initialSelectedItems: selectedTag ? [selectedTag] : undefined,
    onChange: (items) => {
      dispatch(setPlayFilter({ selectedTag: items[0] }));
    },
  });

  const [sort, setSort] = useState<SortQuery | null>({ field: "updatedTimestamp", order: "DESC" });

  const sortOnField = (field: string) => {
    setSort((prevState) => {
      if (!prevState) return { field, order: "ASC" };
      else {
        if (prevState.field === field) {
          if (prevState.order === "DESC") return { field, order: "ASC" };
          else return { field, order: "DESC" };
        } else {
          return { field, order: "ASC" };
        }
      }
    });
  };

  return {
    seasonMultiSelect,
    teamsMultiSelect,
    seriesMultiSelect,
    tagsMultiSelect,
    playNameFilter: selectedPlayNameFilter,
    playNameFilterDebounced,
    onPlayNameFilterChange,
    sort,
    sortOnField,
  };
};

export type UseTableFilters = ReturnType<typeof useTableFilters>;
export type UseTableFiltersSelect = Pick<
  UseTableFilters,
  "seasonMultiSelect" | "teamsMultiSelect" | "seriesMultiSelect" | "tagsMultiSelect" | "playNameFilter"
>;
